import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiError } from '../http/api-client';

export interface HttpError {
  message: string;
  status: number;
  statusText?: string;
  url?: string;
}

export interface ValidationError {
  message: string;
  field?: string;
  errors?: Record<string, string[]>;
}

export interface BusinessError {
  message: string;
  code?: string;
}

export type AppError = HttpError | ValidationError | BusinessError | ApiError | Error;

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlingService {
  /**
   * Extracts error message from various error types
   * Handles: HttpErrorResponse, ApiError, Error, and custom error objects
   */
  extractErrorMessage(error: AppError | HttpErrorResponse | unknown): string {
    if (!error) {
      return 'An unexpected error occurred';
    }

    // Handle HttpErrorResponse
    if (error instanceof HttpErrorResponse) {
      // If error.error is null or undefined, skip error.message and use default status message
      if (error.error === null || error.error === undefined) {
        return this.getDefaultMessageForStatus(error.status);
      }
      return (
        error.error?.Message ||
        error.error?.message ||
        error.error?.error?.message ||
        error.message ||
        this.getDefaultMessageForStatus(error.status)
      );
    }

    // Handle ApiError
    if (this.isApiError(error)) {
      return error.message || 'An error occurred';
    }

    // Handle Error object
    if (error instanceof Error) {
      return error.message || 'An error occurred';
    }

    // Handle objects with message property
    if (typeof error === 'object' && 'message' in error) {
      const errorObj = error as { message?: string };
      return errorObj.message || 'An error occurred';
    }

    // Fallback
    return 'An unexpected error occurred';
  }

  /**
   * Checks if error is an ApiError
   */
  isApiError(error: unknown): error is ApiError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      'status' in error &&
      typeof (error as ApiError).message === 'string'
    );
  }

  /**
   * Checks if error is an HttpErrorResponse
   */
  isHttpError(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse;
  }

  /**
   * Checks if error is a ValidationError
   */
  isValidationError(error: unknown): error is ValidationError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      'errors' in error &&
      typeof (error as ValidationError).message === 'string'
    );
  }

  /**
   * Gets default error message based on HTTP status code
   */
  private getDefaultMessageForStatus(status: number): string {
    const statusMessages: Record<number, string> = {
      400: 'Bad request. Please check your input.',
      401: 'Unauthorized. Please check your credentials.',
      403: 'You do not have permission to perform this action.',
      404: 'The requested resource was not found.',
      409: 'A conflict occurred. The resource may already exist.',
      422: 'Validation error. Please check your input.',
      500: 'Server error. Please try again later.',
      502: 'Bad gateway. The server is temporarily unavailable.',
      503: 'Service unavailable. Please try again later.',
    };

    return statusMessages[status] || `An error occurred (${status})`;
  }

  /**
   * Gets user-friendly error message with fallback
   */
  getUserFriendlyMessage(error: AppError | HttpErrorResponse | unknown, fallback?: string): string {
    const extracted = this.extractErrorMessage(error);
    // If extracted message is the default "unexpected error" and we have a fallback, use fallback
    if (extracted === 'An unexpected error occurred' && fallback) {
      return fallback;
    }
    return extracted || fallback || 'An unexpected error occurred';
  }
}
